import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Wrench, Plus, X, MapPin, CheckCircle, Search, ChevronDown, Trash2 } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';

const PROVINCE_MAP = {
  'Ontario': 'ON', 'Quebec': 'QC', 'British Columbia': 'BC',
  'Alberta': 'AB', 'Manitoba': 'MB', 'Saskatchewan': 'SK',
  'Nova Scotia': 'NS', 'New Brunswick': 'NB',
  'Newfoundland and Labrador': 'NL', 'Prince Edward Island': 'PE',
  'Northwest Territories': 'NT', 'Nunavut': 'NU', 'Yukon': 'YT',
};

function AddressAutocomplete({ onSelect, inputCls }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = (val) => {
    setQuery(val);
    clearTimeout(timerRef.current);
    if (val.length < 4) { setResults([]); setOpen(false); return; }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(val)}&limit=6&lang=en&countrycode=ca`);
        const data = await r.json();
        const items = (data.features || []).filter(f => f.properties?.street || f.properties?.name).slice(0, 5);
        setResults(items); setOpen(items.length > 0);
      } catch { } finally { setLoading(false); }
    }, 320);
  };

  const pick = (feature) => {
    const p = feature.properties;
    const [lng, lat] = feature.geometry.coordinates;
    const houseNum = p.housenumber ? `${p.housenumber} ` : '';
    const street = p.street || p.name || '';
    const address = `${houseNum}${street}`.trim();
    const city = p.city || p.town || p.village || p.county || '';
    const province = PROVINCE_MAP[p.state] || p.state || 'ON';
    onSelect({ address, city, province, lat, lng });
    setQuery(address ? `${address}, ${city}` : city);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
        <input className={`${inputCls} pl-9`} value={query} onChange={e => search(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Search address…" autoComplete="off" />
        {loading && <span className="absolute right-3 top-2.5 text-xs text-gray-400 animate-pulse">searching…</span>}
      </div>
      {open && (
        <ul className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-auto text-sm">
          {results.map((f, i) => {
            const p = f.properties;
            const houseNum = p.housenumber ? `${p.housenumber} ` : '';
            const street = p.street || p.name || '';
            const city = p.city || p.town || p.village || p.county || '';
            const province = PROVINCE_MAP[p.state] || p.state || '';
            return (
              <li key={i} className="px-3 py-2.5 hover:bg-orange-50 cursor-pointer border-b last:border-0"
                onMouseDown={() => pick(f)}>
                <span className="font-medium">{houseNum}{street}</span>
                {city && <span className="text-gray-500"> — {city}{province ? `, ${province}` : ''}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const SERVICE_TYPE_LABELS = { service: 'Service Call', repair: 'Repair', warranty: 'Warranty' };
const SERVICE_TYPE_COLORS = {
  service:  'bg-orange-100 text-orange-700',
  repair:   'bg-red-100 text-red-700',
  warranty: 'bg-blue-100 text-blue-700',
};
const STATUS_COLORS = {
  pending:   'bg-yellow-100 text-yellow-700',
  scheduled: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
}

const emptyForm = {
  sale_id: '',
  contact_name: '',
  address: '',
  city: '',
  province: 'ON',
  lat: '',
  lng: '',
  phone: '',
  service_type: 'service',
  description: '',
  notes: '',
  crew_notes: '',
  scheduled_date: '',
  scheduled_time_start: '',
};

export default function Service() {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [installedSales, setInstalledSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [customerMode, setCustomerMode] = useState('existing'); // 'existing' | 'new'
  const [saleSearch, setSaleSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/service'),
      api.get('/sales?status=completed'),
    ]).then(([svcs, sales]) => {
      setServices(svcs || []);
      // Also get other installed statuses
      return api.get('/sales').then(allSales => {
        const installed = (allSales || []).filter(s =>
          ['completed', 'scheduled', 'in_progress', 'awaiting_payment'].includes(s.status)
        );
        setInstalledSales(installed);
      });
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [field]: val }));
  };

  const openNew = () => {
    setEditId(null);
    setForm(emptyForm);
    setCustomerMode('existing');
    setSaleSearch('');
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openEdit = (svc) => {
    setEditId(svc.id);
    setForm({
      sale_id: svc.sale_id || '',
      contact_name: svc.contact_name || '',
      address: svc.address || '',
      city: svc.city || '',
      province: svc.province || 'ON',
      lat: svc.lat || '',
      lng: svc.lng || '',
      phone: svc.phone || '',
      service_type: svc.service_type || 'service',
      description: svc.description || '',
      notes: svc.notes || '',
      crew_notes: svc.crew_notes || '',
      scheduled_date: svc.scheduled_date || '',
      scheduled_time_start: svc.scheduled_time_start || '',
    });
    setCustomerMode(svc.sale_id ? 'existing' : 'new');
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pickExistingSale = (sale) => {
    setForm(f => ({
      ...f,
      sale_id: sale.id,
      contact_name: sale.customer_name,
      address: sale.customer_address || '',
      city: sale.city || sale.customer_city || '',
      phone: sale.customer_phone || '',
      lat: sale.customer_lat || '',
      lng: sale.customer_lng || '',
    }));
    setSaleSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contact_name.trim()) return alert('Contact name is required');
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        sale_id: form.sale_id ? Number(form.sale_id) : null,
        lat: form.lat ? Number(form.lat) : null,
        lng: form.lng ? Number(form.lng) : null,
      };
      if (editId) {
        await api.put(`/service/${editId}`, payload);
      } else {
        await api.post('/service', payload);
      }
      setShowForm(false);
      setForm(emptyForm);
      setEditId(null);
      load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const markComplete = async (id) => {
    await api.patch(`/service/${id}/status`, { status: 'completed' });
    load();
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete service job for ${name}? This cannot be undone.`)) return;
    await api.delete(`/service/${id}`);
    load();
  };

  const filteredSales = installedSales.filter(s => {
    const q = saleSearch.toLowerCase();
    return !q || s.customer_name?.toLowerCase().includes(q) || s.city?.toLowerCase().includes(q);
  });

  const filteredServices = services.filter(s =>
    filterStatus === 'all' || s.status === filterStatus
  );

  const inputCls = 'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-sm';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1';

  const counts = {
    all: services.length,
    pending: services.filter(s => s.status === 'pending').length,
    scheduled: services.filter(s => s.status === 'scheduled').length,
    completed: services.filter(s => s.status === 'completed').length,
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Wrench className="w-6 h-6 text-orange-500" />
          <h1 className="text-2xl font-bold">Service Calls</h1>
        </div>
        <button onClick={openNew}
          className="bg-orange-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-600 flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Service
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-orange-200 p-5 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-lg">{editId ? 'Edit Service Job' : 'New Service Job'}</h2>
            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Customer section */}
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">Customer</p>

              {/* Toggle */}
              <div className="flex rounded-lg border overflow-hidden text-sm mb-4 w-fit">
                <button type="button"
                  onClick={() => setCustomerMode('existing')}
                  className={`px-4 py-2 font-medium transition-colors ${customerMode === 'existing' ? 'bg-orange-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  Existing Customer
                </button>
                <button type="button"
                  onClick={() => { setCustomerMode('new'); setForm(f => ({ ...f, sale_id: '' })); }}
                  className={`px-4 py-2 font-medium transition-colors ${customerMode === 'new' ? 'bg-orange-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  New Customer
                </button>
              </div>

              {customerMode === 'existing' ? (
                <div>
                  {form.contact_name && form.sale_id ? (
                    <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 mb-2">
                      <div>
                        <p className="font-semibold text-sm text-orange-800">{form.contact_name}</p>
                        {form.city && <p className="text-xs text-orange-600">{form.address ? `${form.address}, ` : ''}{form.city}</p>}
                      </div>
                      <button type="button" onClick={() => setForm(f => ({ ...f, sale_id: '', contact_name: '', address: '', city: '', phone: '' }))}
                        className="text-orange-400 hover:text-orange-600"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                      <input className={`${inputCls} pl-9`} placeholder="Search installed customers…"
                        value={saleSearch} onChange={e => setSaleSearch(e.target.value)} autoComplete="off" />
                    </div>
                  )}
                  {saleSearch && filteredSales.length > 0 && (
                    <ul className="mt-1 border rounded-lg bg-white shadow-sm max-h-52 overflow-auto text-sm">
                      {filteredSales.slice(0, 10).map(s => (
                        <li key={s.id}
                          className="px-3 py-2.5 hover:bg-orange-50 cursor-pointer border-b last:border-0"
                          onClick={() => pickExistingSale(s)}>
                          <span className="font-medium">{s.customer_name}</span>
                          <span className="text-gray-400 text-xs ml-2">{s.city} · {s.total_footage}ft · {s.color_name}</span>
                          <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${STATUS_COLORS[s.status]}`}>{s.status}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {saleSearch && filteredSales.length === 0 && (
                    <p className="text-xs text-gray-400 mt-1">No installed customers found.</p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className={labelCls}>Contact Name *</label>
                    <input className={inputCls} value={form.contact_name} onChange={set('contact_name')} required />
                  </div>
                  <div>
                    <label className={labelCls}>
                      Address
                      {form.lat && form.lng && (
                        <span className="ml-2 inline-flex items-center gap-1 text-xs text-green-600 font-normal">
                          <CheckCircle className="w-3 h-3" /> GPS captured
                        </span>
                      )}
                    </label>
                    <AddressAutocomplete inputCls={inputCls} onSelect={({ address, city, province, lat, lng }) => {
                      setForm(f => ({ ...f, address, city, province, lat, lng }));
                    }} />
                    <p className="text-xs text-gray-400 mt-1">Search to auto-fill, or fill manually below</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Street Address</label>
                      <input className={inputCls} value={form.address} onChange={e =>
                        setForm(f => ({ ...f, address: e.target.value, lat: '', lng: '' }))
                      } placeholder="Street address" />
                    </div>
                    <div>
                      <label className={labelCls}>City</label>
                      <input className={inputCls} value={form.city} onChange={set('city')} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Phone</label>
                    <input className={inputCls} value={form.phone} onChange={set('phone')} />
                  </div>
                </div>
              )}
            </div>

            {/* Service details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Service Type *</label>
                <select className={inputCls} value={form.service_type} onChange={set('service_type')}>
                  <option value="service">Service Call</option>
                  <option value="repair">Repair</option>
                  <option value="warranty">Warranty</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Phone {customerMode === 'existing' && '(override)'}</label>
                <input className={inputCls} value={form.phone} onChange={set('phone')}
                  placeholder={customerMode === 'existing' ? form.phone || 'From customer record' : ''} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Description / Issue *</label>
                <textarea className={`${inputCls} h-20`} value={form.description} onChange={set('description')}
                  placeholder="Describe the issue or service needed…" required />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Internal Notes</label>
                <input className={inputCls} value={form.notes} onChange={set('notes')}
                  placeholder="Internal notes…" />
              </div>
            </div>

            {/* Scheduling */}
            <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
              <p className="text-sm font-semibold text-orange-800 mb-3">Schedule</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>Date</label>
                  <input type="date" className={inputCls} value={form.scheduled_date} onChange={set('scheduled_date')} />
                  <p className="text-xs text-gray-400 mt-1">Leave blank to save as pending</p>
                </div>
                <div>
                  <label className={labelCls}>Start Time</label>
                  <input type="time" className={inputCls} value={form.scheduled_time_start} onChange={set('scheduled_time_start')} />
                </div>
                <div>
                  <label className={labelCls}>Crew Notes</label>
                  <input className={inputCls} value={form.crew_notes} onChange={set('crew_notes')}
                    placeholder="Gate code, special instructions…" />
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <button type="submit" disabled={submitting}
                className="px-5 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50">
                {submitting ? 'Saving…' : editId ? 'Save Changes' : 'Create Service Job'}
              </button>
              <button type="button" onClick={() => setShowForm(false)}
                className="px-5 py-2 bg-white border text-gray-600 rounded-lg text-sm hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-1 mb-4">
        {[
          { key: 'all',       label: 'All' },
          { key: 'pending',   label: 'Pending' },
          { key: 'scheduled', label: 'Scheduled' },
          { key: 'completed', label: 'Completed' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setFilterStatus(tab.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
              filterStatus === tab.key ? 'bg-orange-500 text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'
            }`}>
            {tab.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${filterStatus === tab.key ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-500'}`}>
              {counts[tab.key]}
            </span>
          </button>
        ))}
      </div>

      {/* Service list */}
      {loading ? (
        <p className="text-center text-gray-400 py-12">Loading…</p>
      ) : filteredServices.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center text-gray-400">
          <Wrench className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p>No service jobs yet.</p>
          <button onClick={openNew} className="mt-3 text-sm text-orange-500 hover:underline">Create one →</button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Date</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Customer</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Type</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Description</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredServices.map(svc => (
                <tr key={svc.id} className="hover:bg-orange-50/40 transition-colors">
                  <td className="px-4 py-3">
                    {svc.scheduled_date ? (
                      <>
                        <p className="font-medium">{formatDate(svc.scheduled_date)}</p>
                        {svc.scheduled_time_start && <p className="text-xs text-gray-400">{svc.scheduled_time_start}</p>}
                      </>
                    ) : (
                      <span className="text-xs text-yellow-600 font-medium">Not scheduled</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{svc.display_name || svc.contact_name}</p>
                    {(svc.display_address || svc.display_city) && (
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <MapPin className="w-3 h-3 shrink-0" />
                        {[svc.display_address, svc.display_city].filter(Boolean).join(', ')}
                      </p>
                    )}
                    {svc.display_phone && <p className="text-xs text-gray-400">{svc.display_phone}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${SERVICE_TYPE_COLORS[svc.service_type]}`}>
                      {SERVICE_TYPE_LABELS[svc.service_type]}
                    </span>
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    <p className="truncate text-gray-700">{svc.description || '—'}</p>
                    {svc.crew_notes && <p className="text-xs text-gray-400 italic truncate">{svc.crew_notes}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${STATUS_COLORS[svc.status]}`}>
                      {svc.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {svc.status === 'scheduled' && (
                        <button onClick={() => markComplete(svc.id)}
                          className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium whitespace-nowrap">
                          <CheckCircle className="w-3.5 h-3.5" /> Complete
                        </button>
                      )}
                      <button onClick={() => openEdit(svc)}
                        className="text-xs text-gray-500 hover:text-orange-600 font-medium">
                        Edit
                      </button>
                      {user?.role === 'admin' && (
                        <button onClick={() => handleDelete(svc.id, svc.display_name || svc.contact_name)}
                          className="text-gray-300 hover:text-red-500 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
